import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiFailure } from "../api/client";
import type { Balance, Call, Usage, UsageRow } from "../api/types";
import { useSession } from "../store/session";
import { Badge, Card, ErrorState, Loading, PageHeader } from "../components/ui";
import { BarraDeResultados, Columnas, BarrasOrdenadas, VIZ } from "../components/charts";
import type { Tramo } from "../components/charts";
import { isLive, useInterval } from "./Calls";

// El tablero: lo que alguien quiere ver al abrir el portal por la mañana.
//
// # Todo sale de endpoints reales
//
// CLAUDE.md §6 prohíbe mostrar datos inventados, y eso descarta la tentación
// obvia de un tablero: rellenar los huecos con ceros bonitos. Aquí cada número
// viene de una petición, y si una falla la tarjeta lo dice en lugar de mostrar
// un cero que se leería como "no pasó nada". Lo mismo con las gráficas: sin
// datos no se dibuja un eje vacío, se dice que no hay nada en el periodo.
//
// # De dónde sale cada cosa
//
//   - **Hoy** (activas, ASR, ACD, resultados, reparto por hora) de
//     `/api/v1/calls`, que es la lista que ya se pedía.
//   - **La tendencia** de `/api/v1/usage`, que agrega por día y por destino en
//     la zona horaria de la cuenta. Es el único sitio donde hay historia más
//     allá de las últimas llamadas, y evita inventarse un endpoint nuevo.
//
// # Por qué el ASR y el ACD se calculan aquí
//
// Porque salen de las mismas llamadas que ya se piden para la lista de hoy, y
// un endpoint de agregados sería una consulta más para derivar dos divisiones.
// Si el volumen crece hasta que traer las llamadas del día sea caro, el cálculo
// se mueve a SQL — y el sitio donde se nota es este archivo, no la API.
const POLL_INTERVAL_MS = 10_000;

// callsPerPage acota lo que se pide para los indicadores del día.
//
// Doscientas llamadas son suficientes para que ASR y ACD signifiquen algo y
// poco para que la petición sea barata. Con más tráfico, el indicador se
// convierte en una muestra y hay que decirlo: por eso el aviso de abajo.
const callsPerPage = 200;

/** Rangos del selector de la tendencia. */
const RANGOS = [7, 14, 30] as const;
type Rango = (typeof RANGOS)[number];

export function Dashboard() {
  const can = useSession((s) => s.can);
  const canSeeBilling = can("billing.read");

  const [calls, setCalls] = useState<Call[] | null>(null);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [historia, setHistoria] = useState<Usage | null>(null);
  const [rango, setRango] = useState<Rango>(14);
  const [error, setError] = useState<string | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  const load = useCallback(
    async (silent: boolean) => {
      if (!silent) setError(null);
      try {
        const rows = await api.get<Call[]>(`/api/v1/calls?limit=${callsPerPage}`);
        setCalls(rows ?? []);
        setError(null);
      } catch (err) {
        setError(err instanceof ApiFailure ? err.error.message : "error inesperado");
      }

      if (!canSeeBilling) return;
      try {
        // El consumo de hoy y el del periodo van por separado a propósito: el
        // día lo decide la zona horaria de la cuenta, que es la que aplica el
        // backend, y recortar aquí el rango para sacar «hoy» daría otro número
        // en cuanto la cuenta no esté en la zona del navegador.
        const [bal, use, hist] = await Promise.all([
          api.get<Balance>("/api/v1/balance"),
          api.get<Usage>(`/api/v1/usage?from=${startOfToday()}`),
          api.get<Usage>(`/api/v1/usage?from=${diasAtras(rango)}`),
        ]);
        setBalance(bal);
        setUsage(use);
        setHistoria(hist);
        setBalanceError(null);
      } catch (err) {
        // El saldo falla aparte: un error de facturación no debe dejar el
        // tablero sin las llamadas, que es lo que más se mira.
        setBalanceError(
          err instanceof ApiFailure ? err.error.message : "no se pudo leer el saldo",
        );
      }
    },
    [canSeeBilling, rango],
  );

  useEffect(() => {
    void load(false);
  }, [load]);
  useInterval(() => void load(true), POLL_INTERVAL_MS);

  const today = useMemo(() => (calls ?? []).filter((c) => isToday(c.queued_at)), [calls]);
  const porHora = useMemo(() => llamadasPorHora(today), [today]);
  const resultados = useMemo(() => resultadosDe(today), [today]);
  const serie = useMemo(() => serieDiaria(historia?.rows ?? [], rango), [historia, rango]);
  const destinos = useMemo(() => topDestinos(historia?.rows ?? []), [historia]);

  if (calls === null && error === null) return <Loading />;
  if (calls === null) return <ErrorState message={error ?? ""} onRetry={() => void load(false)} />;

  const active = calls.filter((c) => isLive(c.status));
  const answered = today.filter((c) => c.answered_seconds > 0);
  const finished = today.filter((c) => c.ended_at !== null);

  // ASR: contestadas sobre terminadas. Sobre terminadas y no sobre todas,
  // porque una llamada que todavía está timbrando no es una fallida.
  const asr = finished.length > 0 ? (answered.length / finished.length) * 100 : null;
  // ACD: duración media de las contestadas. De las contestadas y no de todas,
  // porque incluir las que nadie atendió daría una media que no es de ninguna
  // conversación.
  const acd =
    answered.length > 0
      ? answered.reduce((sum, c) => sum + c.answered_seconds, 0) / answered.length
      : null;

  return (
    <div>
      <PageHeader
        title="Tablero"
        description="Lo que está pasando ahora y lo que pasó hoy."
      />

      {error && (
        <Card className="mb-4 border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
          Los indicadores pueden estar desactualizados: {error}
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          label="Llamadas activas"
          value={String(active.length)}
          hint={active.length > 0 ? "en curso ahora mismo" : "ninguna en curso"}
          to="/live"
        />
        <Metric label="Llamadas hoy" value={String(today.length)} hint={`${answered.length} contestadas`} />
        <Metric
          label="ASR"
          value={asr === null ? "—" : `${asr.toFixed(1)} %`}
          hint={
            finished.length === 0
              ? "sin llamadas terminadas hoy"
              : `sobre ${finished.length} terminadas`
          }
        />
        <Metric
          label="ACD"
          value={acd === null ? "—" : formatSeconds(Math.round(acd))}
          hint={
            answered.length === 0
              ? "sin llamadas contestadas hoy"
              : `media de ${answered.length} contestadas`
          }
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="text-sm font-medium text-slate-100">Cómo acabaron las de hoy</h2>
          <p className="mb-3 mt-0.5 text-xs text-slate-500">
            Sobre {today.length} {today.length === 1 ? "llamada" : "llamadas"} de hoy.
          </p>
          <BarraDeResultados tramos={resultados} vacio="Todavía no hay llamadas hoy." />
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-medium text-slate-100">Reparto por hora</h2>
          <p className="mb-3 mt-0.5 text-xs text-slate-500">
            Llamadas iniciadas en cada hora de hoy, hora local.
          </p>
          <Columnas
            datos={porHora}
            vacio="Todavía no hay llamadas hoy."
            titulo="Llamadas por hora del día"
            alto={140}
          />
        </Card>
      </div>

      {canSeeBilling && (
        <section className="mt-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-medium text-slate-100">Tendencia</h2>
            {/* El filtro va en una fila encima de las gráficas, y cambia las
                tres a la vez: tres selectores independientes invitan a comparar
                periodos distintos sin darse cuenta. */}
            <div className="flex gap-1" role="group" aria-label="Periodo">
              {RANGOS.map((dias) => (
                <button
                  key={dias}
                  type="button"
                  onClick={() => setRango(dias)}
                  aria-pressed={rango === dias}
                  className={`rounded-lg px-2.5 py-1 text-xs transition ${
                    rango === dias
                      ? "bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/40"
                      : "text-slate-400 hover:bg-white/[0.06] hover:text-slate-200"
                  }`}
                >
                  {dias} días
                </button>
              ))}
            </div>
          </div>

          {/* Tres gráficas y no una con tres líneas: llamadas, minutos y pesos
              no comparten escala, y meterlas en un eje —o peor, en dos— haría
              comparar alturas que no significan lo mismo. */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="p-4">
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Llamadas por día
              </h3>
              <p className="mb-3 mt-1 text-lg font-semibold tabular-nums text-slate-100">
                {serie.totalLlamadas.toLocaleString("es-MX")}
                <span className="ml-1.5 text-xs font-normal text-slate-500">en el periodo</span>
              </p>
              <Columnas
                datos={serie.llamadas}
                vacio={sinHistoria(balanceError, rango)}
                titulo={`Llamadas por día en los últimos ${rango} días`}
              />
            </Card>

            <Card className="p-4">
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Minutos conversados
              </h3>
              <p className="mb-3 mt-1 text-lg font-semibold tabular-nums text-slate-100">
                {Math.round(serie.totalMinutos).toLocaleString("es-MX")}
                <span className="ml-1.5 text-xs font-normal text-slate-500">min</span>
              </p>
              <Columnas
                datos={serie.minutos}
                formato={(v) => `${v.toLocaleString("es-MX", { maximumFractionDigits: 1 })} min`}
                vacio={sinHistoria(balanceError, rango)}
                titulo={`Minutos conversados por día en los últimos ${rango} días`}
              />
            </Card>

            <Card className="p-4">
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Gasto por día
              </h3>
              <p className="mb-3 mt-1 text-lg font-semibold tabular-nums text-slate-100">
                {serie.totalGasto.toLocaleString("es-MX", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                <span className="ml-1.5 text-xs font-normal text-slate-500">
                  {historia?.currency ?? "MXN"}
                </span>
              </p>
              <Columnas
                datos={serie.gasto}
                formato={(v) =>
                  v.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
                vacio={sinHistoria(balanceError, rango)}
                titulo={`Gasto por día en los últimos ${rango} días`}
              />
            </Card>
          </div>
        </section>
      )}

      {canSeeBilling && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Card className="p-4">
            <h2 className="text-sm font-medium text-slate-100">A dónde se va el gasto</h2>
            <p className="mb-3 mt-0.5 text-xs text-slate-500">
              Destinos con más gasto en el periodo, agrupados por prefijo.
            </p>
            <BarrasOrdenadas
              datos={destinos}
              formato={(v) =>
                v.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              }
              vacio={sinHistoria(balanceError, rango)}
              titulo="Gasto por destino"
            />
          </Card>

          <div className="grid gap-4 sm:grid-cols-2">
            <Card className="p-4">
              <h2 className="text-sm font-medium text-slate-100">Saldo</h2>
              {balanceError ? (
                <p className="mt-3 text-sm text-amber-300">{balanceError}</p>
              ) : balance === null ? (
                <p className="mt-3 text-sm text-slate-500">Cargando…</p>
              ) : (
                <>
                  <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-100">
                    {money(balance.available)}{" "}
                    <span className="text-base font-normal text-slate-500">{balance.currency}</span>
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    disponible · {money(balance.reserved)} comprometido en llamadas en curso
                  </p>
                  {balance.low_balance_threshold_micros !== undefined &&
                    balance.available_micros <= balance.low_balance_threshold_micros && (
                      <p className="mt-2">
                        <Badge tone="bad">Saldo bajo</Badge>
                      </p>
                    )}
                  <Link
                    to="/balance"
                    className="mt-3 inline-block text-xs text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200"
                  >
                    Ver movimientos
                  </Link>
                </>
              )}
            </Card>

            <Card className="p-4">
              <h2 className="text-sm font-medium text-slate-100">Gasto de hoy</h2>
              {usage === null ? (
                <p className="mt-3 text-sm text-slate-500">
                  {balanceError ? "No disponible." : "Cargando…"}
                </p>
              ) : (
                <>
                  <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-100">
                    {money(usage.total)}{" "}
                    <span className="text-base font-normal text-slate-500">{usage.currency}</span>
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    en la zona {usage.timezone} · {usage.rows.length} destinos
                  </p>
                  <Link
                    to="/usage"
                    className="mt-3 inline-block text-xs text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200"
                  >
                    Ver consumo
                  </Link>
                </>
              )}
            </Card>
          </div>
        </div>
      )}

      {calls.length >= callsPerPage && (
        <p className="mt-4 text-xs text-slate-500">
          Los indicadores de hoy se calculan sobre las últimas {callsPerPage} llamadas. Con más
          tráfico que eso en un día, son una muestra y no el total.
        </p>
      )}

      {/* La lista de llamadas en curso **ya no vive aquí**: tiene su propia
          pantalla en `/live`. Aquí estaba cortada a diez filas, filtrada sobre
          la página de llamadas que el tablero ya había traído —así que una
          llamada larga desaparecía cuando entraban llamadas nuevas delante— y
          mezclada con indicadores del día, que se miran una vez y no se
          vigilan. Lo que queda es el contador, que sí es un indicador, y lleva
          a la pantalla que sirve para mirar. */}

      <Documentation />
    </div>
  );
}

/**
 * Metric es una tarjeta de indicador. Con `to`, además lleva a algún sitio.
 *
 * El enlace envuelve la tarjeta entera en lugar de añadir un «ver más» debajo:
 * un número que invita a mirarlo de cerca debe ser clicable donde el dedo ya
 * está, no en un enlace de diez píxeles.
 */
function Metric({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value: string;
  hint: string;
  to?: string;
}) {
  const contenido = (
    <>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-100">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
    </>
  );

  if (to === undefined) return <Card className="p-4">{contenido}</Card>;

  return (
    <Link to={to} className="rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60">
      <Card className="h-full p-4 transition hover:bg-white/[0.06]">{contenido}</Card>
    </Link>
  );
}

/**
 * Documentation enlaza lo que un desarrollador necesita.
 *
 * Son enlaces y no contenido embebido porque la documentación vive en el
 * repositorio y en el contrato: una copia en el portal sería una copia que se
 * queda vieja, y la que se queda vieja es la que alguien lee.
 */
function Documentation() {
  const links = [
    { href: "/docs", label: "Guías de integración" },
    { href: "/swagger/index.html", label: "Referencia de la API (Swagger)" },
    { href: "/openapi.yaml", label: "Contrato OpenAPI" },
  ];

  return (
    <section className="mt-6">
      <h2 className="mb-2 text-sm font-medium text-slate-100">Documentación</h2>
      <Card className="p-4">
        <ul className="space-y-1 text-sm">
          {links.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-500">
          El markup de voz, los Media Streams y los webhooks están documentados en el
          repositorio: <code>docs/iamedia-markup-v1.md</code>,{" "}
          <code>docs/media-stream-v1.md</code> y <code>docs/webhooks-v1.md</code>.
        </p>
      </Card>
    </section>
  );
}

// ------------------------------------------------------------- ayudantes ----

/**
 * resultadosDe reparte las llamadas de hoy por cómo acabaron.
 *
 * Los colores son la paleta de estado, no colores de serie: «contestada» es
 * buena y «fallida» es mala, y eso tiene un significado fijo en todo el portal.
 * Cada tramo lleva su etiqueta al lado, así que el color acompaña pero no es
 * lo único que distingue un resultado de otro.
 */
function resultadosDe(calls: Call[]): Tramo[] {
  const cuenta = (predicado: (c: Call) => boolean) => calls.filter(predicado).length;
  return [
    { etiqueta: "Contestadas", valor: cuenta((c) => c.status === "completed"), color: VIZ.bueno },
    { etiqueta: "Ocupado", valor: cuenta((c) => c.status === "busy"), color: VIZ.serio },
    { etiqueta: "Sin respuesta", valor: cuenta((c) => c.status === "no-answer"), color: VIZ.aviso },
    { etiqueta: "Fallidas", valor: cuenta((c) => c.status === "failed"), color: VIZ.critico },
    { etiqueta: "Canceladas", valor: cuenta((c) => c.status === "canceled"), color: VIZ.neutro },
    {
      etiqueta: "En curso",
      valor: cuenta((c) => isLive(c.status)),
      color: VIZ.serie,
    },
  ];
}

/** llamadasPorHora reparte las de hoy en las 24 horas locales. */
function llamadasPorHora(calls: Call[]): { etiqueta: string; valor: number; detalle: string }[] {
  const horas = new Array<number>(24).fill(0);
  for (const call of calls) {
    const hora = new Date(call.queued_at).getHours();
    if (hora >= 0 && hora < 24) horas[hora] = (horas[hora] ?? 0) + 1;
  }
  return horas.map((valor, hora) => ({
    etiqueta: `${hora}`,
    valor,
    detalle: `${String(hora).padStart(2, "0")}:00 – ${String(hora).padStart(2, "0")}:59`,
  }));
}

/**
 * serieDiaria rellena los días sin consumo.
 *
 * El backend solo devuelve los días que tuvieron tráfico, y una gráfica que se
 * salta los días vacíos miente: comprime el eje y hace parecer continuo lo que
 * fueron tres días sueltos. Aquí el eje son todos los días del periodo.
 */
function serieDiaria(rows: UsageRow[], dias: number) {
  const porDia = new Map<string, { llamadas: number; segundos: number; gasto: number }>();
  for (const row of rows) {
    const dia = row.day.slice(0, 10);
    const previo = porDia.get(dia) ?? { llamadas: 0, segundos: 0, gasto: 0 };
    porDia.set(dia, {
      llamadas: previo.llamadas + row.calls,
      segundos: previo.segundos + row.answered_seconds,
      gasto: previo.gasto + row.charged_micros / 1_000_000,
    });
  }

  const llamadas = [];
  const minutos = [];
  const gasto = [];
  let totalLlamadas = 0;
  let totalMinutos = 0;
  let totalGasto = 0;

  const hoy = new Date();
  for (let i = dias - 1; i >= 0; i--) {
    const fecha = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - i);
    const clave = claveDeDia(fecha);
    const valores = porDia.get(clave) ?? { llamadas: 0, segundos: 0, gasto: 0 };
    const etiqueta = fecha.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
    const detalle = fecha.toLocaleDateString("es-MX", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

    llamadas.push({ etiqueta, valor: valores.llamadas, detalle });
    minutos.push({ etiqueta, valor: valores.segundos / 60, detalle });
    gasto.push({ etiqueta, valor: valores.gasto, detalle });
    totalLlamadas += valores.llamadas;
    totalMinutos += valores.segundos / 60;
    totalGasto += valores.gasto;
  }

  return { llamadas, minutos, gasto, totalLlamadas, totalMinutos, totalGasto };
}

/**
 * topDestinos ordena por gasto y agrupa la cola.
 *
 * Seis filas y un «otros»: a partir de ahí la lista deja de compararse y pasa
 * a leerse, y para leer ya está la pantalla de consumo.
 */
function topDestinos(rows: UsageRow[]) {
  const porDestino = new Map<string, number>();
  for (const row of rows) {
    porDestino.set(row.destination, (porDestino.get(row.destination) ?? 0) + row.charged_micros);
  }

  const ordenados = [...porDestino.entries()]
    .map(([destino, micros]) => ({ destino, gasto: micros / 1_000_000 }))
    .sort((a, b) => b.gasto - a.gasto);

  const principales = ordenados.slice(0, 6).map((fila) => ({
    etiqueta: fila.destino === "" ? "(sin prefijo)" : `+${fila.destino}`,
    valor: fila.gasto,
  }));
  const cola = ordenados.slice(6);
  if (cola.length > 0) {
    principales.push({
      etiqueta: `Otros (${cola.length})`,
      valor: cola.reduce((suma, fila) => suma + fila.gasto, 0),
    });
  }
  return principales;
}

/** sinHistoria distingue «no hubo tráfico» de «no se pudo leer». */
function sinHistoria(error: string | null, dias: number): string {
  return error === null
    ? `Sin consumo en los últimos ${dias} días.`
    : `No se pudo leer el consumo: ${error}`;
}

function claveDeDia(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha.getDate()).padStart(2, "0");
  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** diasAtras devuelve el inicio del día de hace N días, en RFC3339. */
function diasAtras(dias: number): string {
  const now = new Date();
  const desde = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (dias - 1));
  return desde.toISOString();
}

/** startOfToday devuelve el inicio del día local en RFC3339. */
export function startOfToday(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return start.toISOString();
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/**
 * money recorta los micro-decimales que el backend manda y que nadie lee.
 *
 * El backend devuelve seis decimales porque es la precisión con la que cobra;
 * mostrar `0.350000` en una tarjeta es ruido. Se recorta a dos **solo para
 * mostrar**: ningún cálculo pasa por aquí.
 */
export function money(amount: string): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** formatSeconds presenta una duración en minutos y segundos. */
export function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
}
